import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Video, MapPin, Clock, ShieldCheck } from "lucide-react";
import { Header, scrollToId } from "@/components/Header";
import { EnquiryForm } from "@/components/EnquiryForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, SITE_URL } from "@/hooks/useSeo";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1616587226960-4a03badbe8bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const STEPS = [
  { n: "01", title: "Book a free first conversation", body: "Tell us a little about your situation through the enquiry form or give Kevin a call. We reply within one business day to find a time that suits you." },
  { n: "02", title: "Meet Kevin online via video call", body: "You'll receive a secure video call link by email. Join from your laptop, tablet or phone with one click. There's nothing to install on a computer, and you can share your screen if you'd like to walk through a statement together." },
  { n: "03", title: "Share documents securely", body: "Upload statements, payslips and other paperwork through our secure client document upload, or complete the digital fact find in your own time. Everything is sent over HTTPS and encrypted." },
  { n: "04", title: "Receive and discuss your advice", body: "Kevin presents your written advice over video call, explains every recommendation in plain English and answers your questions. Ongoing reviews happen the same way, wherever you live." },
];

const BENEFITS = [
  { Icon: MapPin, title: "Anywhere in Australia", body: "From Perth to Hobart, Darwin to the NSW South Coast. Independent advice no longer depends on your postcode." },
  { Icon: Clock, title: "Fits around your life", body: "No travel, no parking, no time off work. Meet from home, the office or wherever you're comfortable." },
  { Icon: Video, title: "Face to face, on screen", body: "The same personal relationship as an office meeting. You'll see and speak with Kevin every time, never a call centre." },
  { Icon: ShieldCheck, title: "Private and secure", body: "Video calls run on secure, private links, and your documents are sent over HTTPS and encrypted." },
];

const Step = ({ s, i }) => (
  <Reveal delay={i * 0.06}>
    <div data-testid={`online-step-${s.n}`} className="grid grid-cols-12 items-baseline gap-x-4 gap-y-2 border-t border-black/15 px-2 md:px-4 py-8 transition-[background-color] duration-300 hover:bg-[#F9F8F4]">
      <span className="col-span-2 md:col-span-1 font-serif italic text-lg text-[#A78558]">{s.n}</span>
      <h3 className="col-span-10 md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight text-[#111413]">{s.title}</h3>
      <p className="col-span-10 col-start-3 md:col-span-7 md:col-start-6 text-sm leading-relaxed text-[#4A524D]">{s.body}</p>
    </div>
  </Reveal>
);

const Benefit = ({ b, i }) => (
  <Reveal delay={i * 0.06}>
    <div data-testid={`online-benefit-${i + 1}`} className="border-t border-black/15 pt-6">
      <b.Icon size={22} className="text-[#A78558]" />
      <h3 className="mt-4 font-serif text-2xl tracking-tight text-[#111413]">{b.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-[#4A524D]">{b.body}</p>
    </div>
  </Reveal>
);

const SEO = {
  title: "Online Financial Advice Australia Wide via Video Call | NUA Private Wealth",
  description:
    "Independent, fee-for-service financial advice by video meeting. Work with Kevin at NUA Private Wealth by video call from anywhere in Australia: retirement, super, investment and estate planning.",
  path: "/online-advice",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Online financial advice",
      name: "Online Financial Advice",
      url: `${SITE_URL}/online-advice`,
      provider: { "@type": "FinancialService", name: "NUA Private Wealth" },
      areaServed: { "@type": "Country", name: "Australia" },
      availableChannel: { "@type": "ServiceChannel", name: "Video call meeting" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Online Advice", item: `${SITE_URL}/online-advice` },
      ],
    },
  ],
};

const Hero = () => (
  <section data-testid="online-hero" className="relative overflow-hidden bg-[#F9F8F4] pt-16 md:pt-[88px]">
    <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
      <div className="md:col-span-7 pt-10 md:pt-20">
        <motion.img src="/assets/nua-mark-transparent.png" alt="" aria-hidden="true" {...fade(0)} className="mb-8 h-10 md:h-12 w-auto opacity-90" />
        <motion.p {...fade(0.1)} className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
          <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
          Online advice
        </motion.p>
        <h1 data-testid="online-heading" className="mt-8 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] text-[#111413]">
          Independent advice, wherever you are in Australia.
        </h1>
        <motion.p {...fade(0.5)} className="mt-5 font-serif italic text-xl md:text-2xl text-[#0A2333]">
          Meet Kevin online via video call.
        </motion.p>
        <motion.p data-testid="online-subcopy" {...fade(0.7)} className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#4A524D]">
          You don't need to visit our Canberra, Sydney or other city offices to work with us. Through secure video
          meetings, you get the same truly independent, fixed-fee advice and the same
          one-to-one relationship with Kevin, from the comfort of your own home.
        </motion.p>
        <motion.div {...fade(0.85)} className="mt-10 flex flex-wrap items-center gap-5">
          <button data-testid="online-cta-book" onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-7 py-3.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5">
            Book a free video consultation <ArrowUpRight size={15} />
          </button>
          <Link to="/" data-testid="online-cta-home"
            className="text-xs uppercase tracking-[0.15em] text-[#111413] underline decoration-[#A78558] decoration-2 underline-offset-8 hover:text-[#0A2333] transition-colors duration-300">
            Back to home
          </Link>
        </motion.div>
      </div>
      <div className="md:col-span-5 md:pt-12 pb-16 md:pb-24">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-t-[10rem] h-[52vh] md:h-[70vh] border border-black/10">
          <img src={HERO_IMAGE} alt="Client meeting their financial adviser by video call at home" data-testid="online-image"
            className="absolute inset-0 h-full w-full object-cover" />
        </motion.div>
        <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">Advice that travels to you.</p>
      </div>
    </div>
  </section>
);

export default function OnlineAdvicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSeo(SEO);

  return (
    <>
      <Header />
      <main>
        <Hero />
        <section data-testid="online-steps" className="bg-[#EFEBE2] py-16 md:py-36">
          <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <Reveal className="md:sticky md:top-28">
                <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#0A2333]">
                  <span className="h-px w-10 bg-[#0A2333]" aria-hidden="true" />
                  How it works
                </p>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#111413]">Four simple steps, all online.</h2>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              {STEPS.map((s, i) => <Step key={s.n} s={s} i={i} />)}
              <div className="border-t border-black/15" aria-hidden="true" />
            </div>
          </div>
        </section>
        <section data-testid="online-benefits" className="bg-[#F9F8F4] py-16 md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-10">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#111413] max-w-2xl">Why clients choose video meetings.</h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {BENEFITS.map((b, i) => <Benefit key={b.title} b={b} i={i} />)}
            </div>
            <Reveal>
              <p className="mt-14 max-w-2xl text-base leading-relaxed text-[#4A524D]">
                Prefer to meet in person? Kevin is available at our Canberra and Sydney offices mainly, with Melbourne, Brisbane and Perth options too.
                You are welcome to mix both, meeting face to face once and reviewing online after that.
              </p>
            </Reveal>
          </div>
        </section>
        <EnquiryForm />
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
}
